import React, { useMemo, useState } from "react";

import { Autocomplete, CircularProgress, ModalProps, Stack, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { useGeneralQueries } from "../../../queries/query-context";
import { useMeasureParametersSelector, useReportsQueriesSelector } from "../../../store";
import { MeasureParameter, ReportsQueries } from "../../../store/types";
import { GeneralModal } from "../General-modal/GeneralModal";

const graphColors: string[] = ["#066471", "#F1C57B", "#DBE2AD", "#96C59B"];

const options: Highcharts.Options = {
  chart: {
    type: "scatter",
    zoomType: "xy" // TS issue
  } as Highcharts.ChartOptions,
  tooltip: {
    useHTML: true,
    formatter: function () {
      // Find both points of the series
      const dealName = this.series.name;
      const points = this.series.points;
      const pointA = points[0];
      const pointB = points[1];
      const pointAX = Highcharts.numberFormat(pointA.x as number, 2);
      const pointAY = Highcharts.numberFormat(pointA.y as number, 2);
      const pointBY = Highcharts.numberFormat(pointB.y as number, 2);

      return `
        <span>Deal: <b>${dealName}</b></span>
        <br/>
        <span>IRR difference: <b>${pointAX}</b></span>
        <br/>
        <span>${pointA.name}: <b>${pointAY}</b></span>
        <br/>
        <span>${pointB.name}: <b>${pointBY}</b></span>
      `; // Tooltip content
      // return `
      //   <b>Series: ${seriesName}</b><br>
      //   Point A: ${pointA.name} - (${pointA.x}, ${pointA.y})<br>
      //   Point B: ${pointB.name} - (${pointB.x}, ${pointB.y})
      // `;
    }
  },
  accessibility: {
    point: {
      valueSuffix: "%"
    }
  },
  title: {
    text: undefined
  },
  credits: {
    enabled: false
  },
  colors: graphColors,
  plotOptions: {
    scatter: {
      marker: {
        radius: 6.5,
        symbol: "circle",
        states: {
          hover: {
            enabled: true,
            lineColor: "rgb(100,100,100)"
          }
        }
      }
    },
    series: {
      point: {
        events: {
          click: () => {}
        }
      }
    }
  },
  xAxis: {
    title: {
      text: "IRR Difference"
    }
  },
  yAxis: {
    min: 0,
    title: {
      text: "IRR"
    }
  }
};

const GraphContent: React.FC<{ queries: Pick<ReportsQueries, "filters" | "dateRange" | "use_accounting_date"> }> = ({
  queries
}) => {
  const { getIRRGraph } = useGeneralQueries();
  const measureOptions = useMeasureParametersSelector();
  const [measure, setMeasure] = useState<MeasureParameter | undefined>();

  const { data, isLoading } = useQuery(["irr-graph", queries, measure], () => {
    return measure ? getIRRGraph({ ...queries, measureId: measure.id }) : Promise.resolve(null);
  });

  const graphOptions: Highcharts.Options | undefined = useMemo(() => {
    if (data) {
      return {
        ...options,
        series: data
      };
    }
  }, [data]);

  return (
    <Stack direction="column" gap={10} sx={{ minWidth: 800 }}>
      <Autocomplete
        options={measureOptions}
        value={measure}
        disableClearable={true}
        onChange={(_, option) => setMeasure(option)}
        renderInput={(params) => <TextField {...params} placeholder="Select" label="Parameter" />}
        getOptionLabel={(option) => option.name}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: MeasureParameter) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
      />
      {isLoading ? (
        <CircularProgress size={80} sx={{ alignSelf: "center" }} />
      ) : (
        <HighchartsReact
          containerProps={{ style: { height: "100%" } }}
          highcharts={Highcharts}
          options={graphOptions}
        />
      )}
    </Stack>
  );
};

export const IRRGraphModal: React.FC<Pick<ModalProps, "open" | "onClose">> = (
  props: Pick<ModalProps, "open" | "onClose">
) => {
  const { filters, dateRange, use_accounting_date } = useReportsQueriesSelector();
  return (
    <GeneralModal title="IRR graph comparison" {...props} onClose={() => props.onClose?.({}, "backdropClick")}>
      <GraphContent queries={{ filters, dateRange, use_accounting_date }} />
    </GeneralModal>
  );
};
